import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Dialog from '@hobs/components/Dialog';
import { Formik, Form } from 'formik';
import { RaisedButton, GhostButton } from '@hobs/components/Buttons';
import { withTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import {
  getCurrentUserNotificationSettings,
  updateCurrentUserNotificationSettings
} from '@hobs/actions/users';
import Toast from '@hobs/components/Toast';
import { createLoadingSelector } from '@hobs/utils/loading';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';

import './EmailNotificationSettingsModal.scss';
import Switch from '@hobs/components/Switch';
import Loader from '@hobs/components/Loader';
import { EMAIL_NOTIFICATION_TABS } from '@hobs/utils/constants';

class EmailNotificationSettingsModal extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      exerciseNotificationSettings: {},
      globalNotificationSettings: {},
      systemNotificationSettings: {},
      annexes: [],
      selectedTab: 0
    };
  }

  async getNotificationSettings() {
    const { getCurrentUserNotificationSettings } = this.props;

    var response = await getCurrentUserNotificationSettings();

    if (response && response.success && response.payload) {
      this.setState({
        exerciseNotificationSettings:
          response.payload.exerciseNotificationSettings,
        globalNotificationSettings: response.payload.globalNotificationSettings,
        annexes: response.payload.annexes,
        systemNotificationSettings: response.payload.systemNotificationSettings
      });
    } else {
      this.handleLoadingError(
        response && response.payload && response.payload.message
          ? response.payload.message
          : 'Failed to get email notification settings'
      );
    }
  }

  handleLoadingError = message => {
    Toast.error(message);
  };

  onSubmit = (values, actions) => {
    const { t, updateCurrentUserNotificationSettings, onClose } = this.props;

    const model = this.mapSettingsToModel(values);

    updateCurrentUserNotificationSettings(model)
      .then(() => {
        Toast.success(t('Notification settings updated successfully'));
        actions.setSubmitting(false);
        onClose();
      })
      .catch(() => {
        Toast.error(t('Failed to update notification settings'));
        actions.setSubmitting(false);
      });
  };

  mapSettingsToModel(values) {
    const model = {
      exerciseNotificationSettings: {},
      globalNotificationSettings: {},
      systemNotificationSettings: {},
      hiddenAnnexIds: values.annexes
        ? values.annexes.filter(x => !x.value).flatMap(x => x.annexId)
        : []
    };

    values.exerciseNotificationSettings.forEach(obj => {
      model.exerciseNotificationSettings[obj.propName] = obj.value;
    });

    if (values.globalNotificationSettings) {
      values.globalNotificationSettings.forEach(obj => {
        model.globalNotificationSettings[obj.propName] = obj.value;
      });
    }

    if (values.systemNotificationSettings) {
      values.systemNotificationSettings.forEach(obj => {
        model.systemNotificationSettings[obj.propName] = obj.value;
      });
    }

    return model;
  }

  getSwitch(value, propName, stateObjectName) {
    const { t } = this.props;

    return (
      <Switch
        label={value ? t('Yes') : t('No')}
        checked={value}
        onChange={event =>
          this.updateStateField(event, propName, stateObjectName)
        }
      />
    );
  }

  updateStateField(value, propName, stateObjectName) {
    var stateCopy = JSON.parse(JSON.stringify(this.state[stateObjectName]));

    var field = stateCopy.find(x => x.propName === propName);

    if (field) {
      field.value = value;
      this.setState({ [stateObjectName]: stateCopy });
    }
  }

  renderSettings(settings, stateObjectName) {
    if (!Object.keys(settings).length) {
      return (
        <div class="bp3-callout">
          <span>
            Subscription to mail notifications is only available when you are
            participating in the exercise.
          </span>
        </div>
      );
    }

    return settings.map((setting, index) => {
      return (
        <>
          <div className="notification-settings--list--item">
            <span>{setting.name}</span>
            {this.getSwitch(setting.value, setting.propName, stateObjectName)}
          </div>
          {settings.length > index + 1 ? <hr /> : null}
        </>
      );
    });
  }

  getPropertyName(obj, expression) {
    var res = {};
    Object.keys(obj).map(k => {
      res[k] = k;
    });
    return expression(res);
  }

  renderAnnexSettings(settings) {
    if (!settings.length) {
      return (
        <div class="bp3-callout">
          <span>
            Subscription to mail notifications is only available when you are
            participating in the exercise.
          </span>
        </div>
      );
    }

    return settings.map((setting, index) => {
      const { t } = this.props;

      return (
        <>
          <div className="notification-settings--list--item">
            <span>{setting.name}</span>
            <Switch
              label={setting.value ? t('Yes') : t('No')}
              checked={setting.value}
              onChange={event =>
                this.updateStateField(
                  event,
                  setting.name,
                  this.getPropertyName(this.state, x => x.annexes)
                )
              }
            />
          </div>
          {settings.length > index + 1 ? <hr /> : null}
        </>
      );
    });
  }

  renderForm(isSubmitting, values, onClose) {
    const { isLoading } = this.props;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <Form>
        <TabStrip
          animation={false}
          className="notification-settings--tab-strip"
          selected={this.state.selectedTab}
          onSelect={e => this.setState({ selectedTab: e.selected })}
        >
          <TabStripTab title="Current exercise">
            <div className="notification-settings--list">
              {this.renderSettings(
                values.exerciseNotificationSettings,
                this.getPropertyName(
                  this.state,
                  x => x.exerciseNotificationSettings
                )
              )}
            </div>
          </TabStripTab>
          {values.globalNotificationSettings &&
            Object.keys(values.globalNotificationSettings).length && (
              <TabStripTab title="Global">
                <div className="notification-settings--list">
                  {this.renderSettings(
                    values.globalNotificationSettings,
                    this.getPropertyName(
                      this.state,
                      x => x.globalNotificationSettings
                    )
                  )}
                </div>
              </TabStripTab>
            )}
          {values.annexes && Object.keys(values.annexes).length && (
            <TabStripTab title="Annex settings">
              <div className="notification-settings--list">
                {this.renderAnnexSettings(values.annexes)}
              </div>
            </TabStripTab>
          )}
          {values.systemNotificationSettings &&
            Object.keys(values.systemNotificationSettings).length && (
              <TabStripTab title="System">
                <div className="notification-settings--list">
                  {this.renderSettings(
                    values.systemNotificationSettings,
                    this.getPropertyName(
                      this.state,
                      x => x.systemNotificationSettings
                    )
                  )}
                </div>
              </TabStripTab>
            )}
        </TabStrip>
        <div className="notification-settings--actions">
          <GhostButton onClick={onClose} disabled={isSubmitting}>
            Cancel
          </GhostButton>
          {!(
            this.state.selectedTab ===
              EMAIL_NOTIFICATION_TABS.CurrentExercise &&
            !Object.keys(this.state.exerciseNotificationSettings).length
          ) && (
            <RaisedButton type="submit" disabled={isSubmitting}>
              Save settings
            </RaisedButton>
          )}
        </div>
      </Form>
    );
  }

  render() {
    const { isOpen, onClose, t } = this.props;

    return (
      <Dialog
        title="Email notification settings"
        isOpen={isOpen}
        onClose={onClose}
        onOpening={async () => await this.getNotificationSettings()}
      >
        <Formik
          enableReinitialize={true}
          initialValues={this.state}
          onSubmit={this.onSubmit}
        >
          {({ isSubmitting, values }) => {
            return (
              <div className="notification-settings">
                <span className="notification-settings--description">
                  {t(
                    'You can subscribe to one or several actions to receive email, when it happens'
                  )}
                </span>
                {this.renderForm(isSubmitting, values, onClose)}
              </div>
            );
          }}
        </Formik>
      </Dialog>
    );
  }
}

EmailNotificationSettingsModal.propTypes = {
  isOpen: PropTypes.bool,
  t: PropTypes.func,
  onClose: PropTypes.func,
  getCurrentUserNotificationSettings: PropTypes.func,
  updateCurrentUserNotificationSettings: PropTypes.func
};

const isLoading = createLoadingSelector(['Users/GetNotificationSettings']);

const mapDispatchToProps = {
  getCurrentUserNotificationSettings,
  updateCurrentUserNotificationSettings
};

const mapStateToProps = state => ({
  isLoading: isLoading(state)
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(EmailNotificationSettingsModal));
